import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import { IntlShape } from 'react-intl';
import { CommonModal } from '../../../../../global/modal/common/common-modal';
import {
    NAME_PRONUNCIATION_TEXT,
    RECORD_NAME_PRONUNCIATION_AUDIO_TEXT,
    UPLOAD_NAME_PRONUNCIATION_AUDIO_FILE_TEXT
} from '../rolodex-contact-text';
import SoundSVG from '../../../../../../images/svg/sound';
import { UploadAudioS3Presentation } from '../../upload-audio/upload-audio-to-s3.presentation';
import { FormFieldProps } from '../../../../../../infrastructure/form-viewmodel-controller';
import { RolodexContactFormVM } from '../rolodex-contact-form.viewmodel';
import DefaultButton from '../../../../../global/default-button/default-button';
import { ADD_TEXT, CANCEL_TEXT } from '../../../../../../constants/common-labels.constants';
import { RecordAudioWave } from '../../../../../global/record-audio/record-audio-wave';

import './name-pronunciation-popup.scss';

type TNamePronunciationPopupMode = null | 'record' | 'upload';
interface IContactFields {
    firstName: FormFieldProps;
    lastName: FormFieldProps;
    title?: FormFieldProps;
    organizationName?: string;
    namePhonetic?: FormFieldProps;
}

interface INamePronunciationPopupProps {
    popupMode: TNamePronunciationPopupMode;
    onClose: () => void;
    contactFields: IContactFields;
    intl: IntlShape;
    vm: RolodexContactFormVM;
}

const NamePronunciationPopup = observer(({
    popupMode,
    onClose,
    contactFields,
    intl,
    vm,
}: INamePronunciationPopupProps) => {
    const {
        firstName,
        lastName,
        organizationName,
        title,
        namePhonetic,
    } = contactFields;
    const isRecordAudioMode = popupMode === 'record';
    const [recordedFile, setRecordedFile] = useState<File | null | undefined>(undefined);

    useEffect(() => {
        if (popupMode !== 'record') {
            setRecordedFile(undefined);
        }
    }, [popupMode]);
    const modalTitleText = isRecordAudioMode
        ? intl.formatMessage(RECORD_NAME_PRONUNCIATION_AUDIO_TEXT)
        : intl.formatMessage(UPLOAD_NAME_PRONUNCIATION_AUDIO_FILE_TEXT)
    ;
    const fullName = `${firstName.value} ${lastName.value}`;

    const onAddHandler = () => {
        vm.uploadAudioFileToContact(recordedFile);
    };
    const closePopup = (): void => {
        if (vm.isSaving) {
            return;
        }
        onClose();
    };

    return (
        <CommonModal
            className='name-pronunciation-popup'
            overlayClassName='name-pronunciation-popup__overlay'
            isOpen={!!popupMode}
            onRequestClose={closePopup}
            title={<span>{modalTitleText} <SoundSVG /></span>}
            footer={(
                <div className='buttons-block buttons-block--from-right'>
                    <DefaultButton
                        modifier={['fat', 'confirm-safe']}
                        text={intl.formatMessage(ADD_TEXT)}
                        disabled={
                            vm.isSaving
                            || vm.uploadAudioVM.isAudioUploading
                            || vm.uploadAudioVM.isAudioChecking
                            || (isRecordAudioMode ? !recordedFile : !vm.uploadAudioVM.hasNewlyUploadedAudio())
                        }
                        onClick={onAddHandler}
                    />
                    <DefaultButton
                        modifier='cancel'
                        text={intl.formatMessage(CANCEL_TEXT)}
                        disabled={vm.isSaving}
                        onClick={closePopup}
                    />
                </div>
            )}
        >
            <div
                className='overflow-ellipsis name-pronunciation-popup__fullname'
                title={fullName}
            >
                {fullName}
            </div>
            {organizationName &&
                <div
                    className='overflow-ellipsis name-pronunciation-popup__info-text name-pronunciation-popup__organization'
                    title={organizationName}
                >
                    {organizationName}
                </div>
            }
            {title?.value &&
                <div
                    className='overflow-ellipsis name-pronunciation-popup__info-text name-pronunciation-popup__title'
                    title={title.value as string}
                >
                    {title.value}
                </div>
            }
            <input
                value={namePhonetic?.value as string ?? ''}
                className='oe-form-field__control w-100 m-top-10px m-bottom-40px name-pronunciation-popup__name-pronunciation'
                type='text'
                disabled
                readOnly
                onChange={() => undefined}
            />
            <div className='oe-form__row'>
                {popupMode === 'record' && (
                    <RecordAudioWave
                        onChange={setRecordedFile}
                    />
                )}
                {popupMode === 'upload' && (
                    <UploadAudioS3Presentation
                        vm={vm.uploadAudioVM}
                        fieldName={intl.formatMessage(NAME_PRONUNCIATION_TEXT)}
                        disabled={vm.saving}
                        onDeleteFile={vm.onDeleteNamePronunciationAudioFile}
                    />
                )}
            </div>
        </CommonModal>
    );
});

export {
    NamePronunciationPopup,
    INamePronunciationPopupProps,
    TNamePronunciationPopupMode
};
